import React, { useEffect, useMemo, useRef, useState } from 'react'
import useDispatch from '~editor/hooks/useDispatch'
import {
  AnyActionType,
  PlayAction,
  PlaySounds,
  IActionComponentProps,
} from '~core/forge.type'
import { Text, Box, Button, SimpleGrid } from '@chakra-ui/react'
import StyledNumberInput from '~editor/components/StyledNumberInput'
import { Select } from '~editor/components/Select'
import { components } from 'react-select'
import { PlayIcon, PauseIcon } from '~editor/components/icons'
import { useUser } from '~editor/hooks/useUser'
import { AclEnum } from '~core/global.type'

interface IProps extends IActionComponentProps {
  action: PlayAction
}

export const PlayActionComponent = ({
  action,
  actions,
  activeActionGesture,
  isInspectorPanel,
  isDisabled,
}: IProps) => {
  const dispatch = useDispatch()
  const user = useUser()
  const PlaySoundsOptions = PlaySounds.filter((playSound) => {
    if (playSound?.acl?.includes?.(AclEnum.STAFF) && !user?.is_staff) {
      return false
    }

    return true
  })

  const selectedSound = useMemo(() => {
    return PlaySounds.find((playSound) => playSound.value === action.sound)
  }, [action.sound])

  const Option = useMemo(() => {
    return (
      // props: Parameters<typeof components.Option>[0]
      props: any,
    ) => {
      const audio = useRef<HTMLAudioElement>()
      const [isPreviewPlaying, setIsPreviewPlaying] = useState(false)

      useEffect(() => {
        return () => {
          if (audio.current) {
            try {
              audio.current.pause()
            } catch (error) {}
          }
        }
      }, [])

      return (
        <components.Option {...props}>
          <Button
            variant="icon"
            paddingLeft="0"
            onClick={async (e) => {
              e.preventDefault()
              e.stopPropagation()
              try {
                if (isPreviewPlaying && audio.current) {
                  audio.current.pause()
                } else {
                  const data = await (window as any).globalThis.api.play({
                    sound: props.data.value,
                    volume: action.volume,
                  })
                  audio.current = data.controller

                  setIsPreviewPlaying(true)

                  if (audio.current) {
                    setTimeout(
                      () => setIsPreviewPlaying(false),
                      data.totalDuration,
                    )
                  }
                }
              } catch (error) {}

              return false
            }}
            color={isPreviewPlaying ? 'gameglass.50' : 'ui.text.grey'}
            _hover={{
              color: 'gameglass.50',
            }}
            _active={{
              color: 'gameglass.50',
            }}
          >
            {isPreviewPlaying ? <PauseIcon /> : <PlayIcon />}
          </Button>
          {props.label}
        </components.Option>
      )
    }
  }, [action])

  return (
    <SimpleGrid
      flex="1"
      templateColumns={!isInspectorPanel ? '1fr auto auto' : '1fr auto'}
      alignItems="center"
      gap="12px"
    >
      <Box display="flex" flex="1" alignItems="center">
        <Select
          captureMenuScroll={false}
          width={!isInspectorPanel ? '100%' : '100px'}
          height="32px"
          options={PlaySoundsOptions}
          defaultValue={PlaySoundsOptions[0]}
          isSearchable={false}
          value={selectedSound}
          components={{ Option }}
          isDisabled={isDisabled}
          styles={{
            container() {
              return {
                width: !isInspectorPanel ? '100%' : '100px',
              }
            },
            menu() {
              if (isInspectorPanel) {
                return {
                  left: 'auto',
                  right: '0',
                }
              }

              return {}
            },
            menuList() {
              if (isInspectorPanel) {
                return {
                  maxHeight: '200px',
                }
              }

              return {}
            },
            option(provided, state) {
              if (isInspectorPanel) {
                return {
                  paddingLeft: '10px',
                  paddingRight: '10px',
                }
              }

              return {}
            },
            control(provided, state) {
              if (isInspectorPanel) {
                return {
                  paddingLeft: '10px !important',
                  paddingRight: '10px !important',
                }
              }

              return {}
            },
          }}
          onChange={(option) => {
            if (!option) return

            dispatch.nodes.updateActions({
              actionGestureType: activeActionGesture,
              actions: actions.map((_a: AnyActionType) => {
                const _action = { ..._a } as PlayAction

                if (action.id === _action.id) {
                  _action.sound = (option as any).value
                }

                return _action
              }),
            })
          }}
        />
      </Box>

      {!isInspectorPanel && (
        <Text color="ui.text.grey" fontWeight="700">
          {'Volume:'}
        </Text>
      )}
      <Box>
        <StyledNumberInput
          fontSize="inherit"
          min={1}
          max={100}
          value={`${action.volume}%`}
          onChange={(_, value) => {
            dispatch.nodes.updateActions({
              actionGestureType: activeActionGesture,
              actions: actions.map((_a: AnyActionType) => {
                const _action = { ..._a } as PlayAction

                if (action.id === _action.id) {
                  _action.volume = value
                }

                return _action
              }),
            })
          }}
        />
      </Box>
    </SimpleGrid>
  )
}

export default PlayActionComponent
